import React from 'react';
import { listFeeds } from '@services/feeds';

// The feed ids GET /feeds suggests, fetched once on mount. Deliberately non-blocking: the feed field
// accepts a typed or pasted id either way, so a slow or failed list costs suggestions, not the page.
//
// `reload` matters more than it looks. The endpoint fails open on a bad multi-input registry — the
// score feeds come back, the multi-input ones don't, and the response is a 200 either way — and the
// registry is loaded per server process. So a list that came back short or empty can be perfectly
// fine on the next call, and without a retry the analyst is stuck with the bad one for the whole
// session. It is stable across renders because it is passed down as a prop to the picker.
export const useFeeds = () => {
  const [feeds, setFeeds] = React.useState(null);
  const [isLoading, setIsLoading] = React.useState(true);
  const [error, setError] = React.useState(null);

  const load = React.useCallback(async (status) => {
    setIsLoading(true);
    const { feeds: list, error: loadError, aborted } = await listFeeds();
    // Unmounted or superseded: the caller no longer exists to be told anything.
    if (status?.isCanceled) return;
    // Aborted is different — this hook is still mounted, it just has no answer. Only the loading
    // flag gets cleared, so the last good list survives and the retry beside it stays reachable;
    // leaving the flag set would spin "Loading the feed list…" for the rest of the session.
    if (aborted) {
      setIsLoading(false);
      return;
    }
    setFeeds(list);
    setError(loadError);
    setIsLoading(false);
  }, []);

  React.useEffect(() => {
    const status = { isCanceled: false };
    load(status);
    return () => {
      status.isCanceled = true;
    };
  }, [load]);

  const reload = React.useCallback(() => load(), [load]);

  return { feeds, isLoading, error, reload };
};
